// src/utils/timetablePDF.js
import { notify } from '../utils/notify';
// Generates a print-ready timetable for any date range (no colors)

const DAY_NAMES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const MBBS_YEAR = { 1: '1st Yr', 2: '2nd Yr', 3: '3rd Yr', 4: '4th Yr' };

function toLocalISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Returns 0=Mon … 6=Sun */
function localDayOfWeek(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  const raw = new Date(y, m - 1, d).getDay(); // 0=Sun
  return (raw + 6) % 7; // shift so Mon=0 … Sun=6
}

/** Advance an ISO date string by n days (local time, no UTC drift) */
function addDaysISO(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + n);
  return toLocalISO(dt);
}

/** Get the Monday of the week containing refISO */
function getWeekDates(refISO) {
  const dow = localDayOfWeek(refISO);          // 0=Mon
  const monday = addDaysISO(refISO, -dow);     // step back to Monday
  return Array.from({ length: 6 }, (_, i) => addDaysISO(monday, i)); // Mon–Sat
}

function fmtDateShort(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${String(y).slice(2)}`;
}

function fmtDateFull(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

function fmtTime(t) {
  if (!t) return '';
  const [h, min] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr % 12 || 12}.${min}${hr >= 12 ? 'PM' : 'AM'}`;
}

/**
 * Build the list of calendar dates to export.
 *
 * Priority:
 *  1. fromDate + toDate  → all Mon–Sat days in that inclusive range
 *  2. weekRef            → Mon–Sat of that week
 *  3. fallback           → Mon–Sat of the current week
 *
 * Sundays are always excluded.
 */
function buildDateList(fromDate, toDate, weekRef) {
  if (fromDate && toDate && fromDate <= toDate) {
    const dates = [];
    let cur = fromDate;
    let guard = 0;
    while (cur <= toDate && guard++ < 730) {   // max 2 years safety
      if (localDayOfWeek(cur) !== 6) {         // skip Sunday (6)
        dates.push(cur);
      }
      cur = addDaysISO(cur, 1);
    }
    return dates;
  }
  // Fall back to single week
  return getWeekDates(weekRef || toLocalISO(new Date()));
}

// ─── Main export function ──────────────────────────────────────────────────────
/**
 * @param {{
 *   classes: Object[],
 *   title?: string,
 *   institutionName?: string,
 *   fromDate?: string,          // ISO — range start (inclusive)
 *   toDate?: string,            // ISO — range end   (inclusive)
 *   weekRef?: string,           // fallback: any ISO date in the desired week
 *   showFaculty?: boolean,
 *   showStatus?: boolean,
 * }} options
 */
export function exportTimetableToPDF({
  classes,
  title           = 'Class Timetable',
  institutionName = 'Medical College ERP',
  fromDate,
  toDate,
  weekRef,
  showFaculty     = true,
  showStatus      = true,
}) {
  // ── 1. Resolve date list ────────────────────────────────────────────────────
  const allDates = buildDateList(fromDate, toDate, weekRef);

  if (allDates.length === 0) {
    notify.info('No valid dates in the selected range.');
    return;
  }

  const rangeLabel = allDates.length === 1
    ? fmtDateFull(allDates[0])
    : `${fmtDateShort(allDates[0])} – ${fmtDateShort(allDates[allDates.length - 1])}`;

  // ── 2. Filter classes to the date range ────────────────────────────────────
  const rangeSet  = new Set(allDates);
  const inRange   = classes.filter(c => c.start_date && rangeSet.has(c.start_date));

  // ── 3. Collect & sort unique time slots ────────────────────────────────────
  const slotMap = new Map();
  inRange.forEach(c => {
    const key = c.time_slot_name || '__unslotted__';
    if (!slotMap.has(key)) {
      slotMap.set(key, {
        key,
        name:  c.time_slot_name || 'Unscheduled',
        start: c.time_slot_start || null,
        end:   c.time_slot_end   || null,
      });
    }
  });

  // If no classes at all, still render the empty table so the layout is clear
  if (slotMap.size === 0) {
    slotMap.set('__unslotted__', { key: '__unslotted__', name: 'No classes', start: null, end: null });
  }

  const timeSlots = Array.from(slotMap.values()).sort((a, b) => {
    if (!a.start) return  1;
    if (!b.start) return -1;
    return a.start.localeCompare(b.start);
  });

  // ── 4. Build grid[date][slotKey] = [class, ...] ───────────────────────────
  const grid = {};
  allDates.forEach(d => {
    grid[d] = {};
    timeSlots.forEach(s => { grid[d][s.key] = []; });
  });
  inRange.forEach(c => {
    const slotKey = c.time_slot_name || '__unslotted__';
    if (!grid[c.start_date]) grid[c.start_date] = {};
    if (!grid[c.start_date][slotKey]) grid[c.start_date][slotKey] = [];
    grid[c.start_date][slotKey].push(c);
  });

  // ── 5. Column widths ────────────────────────────────────────────────────────
  const dayColPct  = 7;
  const slotColPct = ((100 - dayColPct) / timeSlots.length).toFixed(2);

  // ── 6. Header row ───────────────────────────────────────────────────────────
  const thSlots = timeSlots.map(slot => {
    const timeRange = slot.start
      ? `<span class="slot-time">${fmtTime(slot.start)} – ${fmtTime(slot.end)}</span><br>`
      : '';
    return `<th class="th-slot">${timeRange}<span class="slot-name">${slot.name}</span></th>`;
  }).join('');

  // ── 7. Body rows (one per calendar day) ────────────────────────────────────
  const bodyRows = allDates.map(date => {
    const dow = localDayOfWeek(date);   // 0=Mon … 5=Sat
    const dayLabel = DAY_NAMES[dow] || '?';

    const cells = timeSlots.map(slot => {
      const cellItems = grid[date]?.[slot.key] || [];
      const cellHTML  = cellItems.map(c => {
        const statusText  = showStatus  ? `<span class="cls-status">${c.status || ''}</span>` : '';
        const facultyText = showFaculty && c.assigned_to_name
          ? `<div class="cls-faculty">${c.assigned_to_name}</div>` : '';
        const yearBadge   = `<span class="cls-year">${MBBS_YEAR[c.mbbs_year] || ''}</span>`;
        return `
          <div class="cls-item">
            <div class="cls-header">
              <span class="cls-dept">${c.department_code || ''}</span>
              ${yearBadge}
              ${statusText}
            </div>
            <div class="cls-title">${c.title || ''}</div>
            ${facultyText}
          </div>`;
      }).join('');
      return `<td class="td-cell">${cellHTML || '<span class="empty-cell">—</span>'}</td>`;
    }).join('');

    const [y, m, d] = date.split('-').map(Number);
    const dateDisplay = `${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.${y}`;

    return `
      <tr>
        <td class="td-day">
          <div class="day-name">${dayLabel}</div>
          <div class="day-date">${dateDisplay}</div>
        </td>
        ${cells}
      </tr>`;
  }).join('');

  // ── 8. Stats footer ─────────────────────────────────────────────────────────
  const statusCounts = { PENDING: 0, ONGOING: 0, COMPLETED: 0, CANCELLED: 0 };
  inRange.forEach(c => { if (statusCounts[c.status] !== undefined) statusCounts[c.status]++; });

  const statsHTML = `
    <div class="stats-bar">
      <span>Date range: <strong>${rangeLabel}</strong></span>
      <span>Days shown: <strong>${allDates.length}</strong></span>
      <span>Total classes: <strong>${inRange.length}</strong></span>
      <span>Pending: <strong>${statusCounts.PENDING}</strong></span>
      <span>Ongoing: <strong>${statusCounts.ONGOING}</strong></span>
      <span>Completed: <strong>${statusCounts.COMPLETED}</strong></span>
      <span>Cancelled: <strong>${statusCounts.CANCELLED}</strong></span>
    </div>`;

  // ── 9. Full HTML document ───────────────────────────────────────────────────
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${title} — ${rangeLabel}</title>
  <style>
    @page { size: A4 landscape; margin: 8mm 10mm; }
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Segoe UI', Arial, Helvetica, sans-serif;
      font-size: 8.5px;
      color: #000;
      background: #fff;
    }

    /* ── Page header ── */
    .page-header {
      text-align: center;
      border-bottom: 2px solid #000;
      padding-bottom: 5px;
      margin-bottom: 8px;
    }
    .page-header .inst-name {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: .08em;
      color: #333;
    }
    .page-header h1 {
      font-size: 15px;
      font-weight: 800;
      margin: 3px 0 2px;
      text-transform: uppercase;
      letter-spacing: .04em;
    }
    .page-header .range-label {
      font-size: 9px;
      color: #444;
    }

    /* ── Stats bar ── */
    .stats-bar {
      display: flex;
      gap: 16px;
      flex-wrap: wrap;
      font-size: 8px;
      color: #333;
      margin-bottom: 6px;
      padding: 4px 6px;
      border: 1px solid #ccc;
      border-radius: 2px;
      background: #fafafa;
    }
    .stats-bar span { white-space: nowrap; }

    /* ── Table ── */
    table {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    col.col-day  { width: ${dayColPct}%; }
    col.col-slot { width: ${slotColPct}%; }

    th, td {
      border: 1px solid #444;
      vertical-align: top;
      padding: 0;
    }

    /* ── Header cells ── */
    thead tr { background: #f0f0f0; }
    th.th-day-hdr {
      text-align: center;
      font-size: 7.5px;
      font-weight: 700;
      padding: 4px 3px;
      vertical-align: middle;
      line-height: 1.4;
      background: #e8e8e8;
      border: 1px solid #333;
    }
    th.th-slot {
      text-align: center;
      padding: 4px 3px;
      border: 1px solid #333;
      line-height: 1.4;
    }
    .slot-time {
      display: block;
      font-size: 7.5px;
      font-weight: 700;
      color: #000;
    }
    .slot-name {
      display: block;
      font-size: 7px;
      color: #333;
      font-style: italic;
    }

    /* ── Day cell (first column) ── */
    .td-day {
      text-align: center;
      vertical-align: middle;
      background: #f5f5f5;
      border: 1px solid #333;
      padding: 4px 2px;
    }
    .day-name {
      font-size: 9px;
      font-weight: 800;
      letter-spacing: .04em;
    }
    .day-date {
      font-size: 7px;
      color: #444;
      margin-top: 2px;
    }

    /* ── Class cells ── */
    .td-cell {
      padding: 3px;
      border: 1px solid #bbb;
      min-height: 35px;
    }

    .empty-cell {
      font-size: 8px;
      color: #ccc;
      display: block;
      text-align: center;
      padding-top: 4px;
    }

    /* ── Class item block ── */
    .cls-item {
      margin-bottom: 5px;
      padding: 3px 4px;
      border-left: 2px solid #555;
      line-height: 1.35;
    }
    .cls-item:last-child { margin-bottom: 0; }

    .cls-header {
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 2px;
      flex-wrap: wrap;
    }
    .cls-dept {
      font-size: 8px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: .03em;
    }
    .cls-year {
      font-size: 6.5px;
      color: #555;
      border: 1px solid #aaa;
      padding: 0 3px;
      border-radius: 2px;
      white-space: nowrap;
    }
    .cls-status {
      font-size: 6.5px;
      font-weight: 700;
      border: 1px solid #999;
      padding: 0 3px;
      border-radius: 2px;
      margin-left: auto;
      white-space: nowrap;
    }
    .cls-title {
      font-size: 7.5px;
      color: #111;
      line-height: 1.35;
    }
    .cls-faculty {
      font-size: 7px;
      color: #444;
      font-style: italic;
      margin-top: 2px;
    }

    /* ── Week separator (visual break between weeks) ── */
    tr.week-sep td {
      background: #e8e8e8;
      font-size: 7.5px;
      font-weight: 700;
      text-align: center;
      padding: 2px 4px;
      color: #333;
      border: 1px solid #bbb;
    }

    /* ── Footer ── */
    .page-footer {
      margin-top: 6px;
      font-size: 7px;
      color: #666;
      display: flex;
      justify-content: space-between;
      border-top: 1px solid #ccc;
      padding-top: 4px;
    }

    /* ── Print adjustments ── */
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="page-header">
    <div class="inst-name">${institutionName}</div>
    <h1>${title}</h1>
    <div class="range-label">${rangeLabel}</div>
  </div>

  ${statsHTML}

  <table>
    <colgroup>
      <col class="col-day">
      ${timeSlots.map(() => `<col class="col-slot">`).join('')}
    </colgroup>
    <thead>
      <tr>
        <th class="th-day-hdr">DAY &amp;<br>DATE</th>
        ${thSlots}
      </tr>
    </thead>
    <tbody>
      ${bodyRows}
    </tbody>
  </table>

  <div class="page-footer">
    <span>${institutionName} — ${title} (${rangeLabel})</span>
    <span>Generated: ${new Date().toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })}</span>
  </div>

  <script>
    window.onload = function () { setTimeout(function () { window.print(); }, 600); };
  </script>
</body>
</html>`;

  const pw = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes');
  if (!pw) {
    notify.success('Pop-up blocked. Please allow pop-ups for this site and try again.');
    return;
  }
  pw.document.open();
  pw.document.write(html);
  pw.document.close();
}